/**
 * Shared contracts for the persistent task-export job pipeline.
 *
 * The deterministic `jobId` lets the web client subscribe to job state
 * before the cloud function has even started writing, and lets Cloud
 * Functions de-duplicate concurrent clicks for the same task.
 *
 * Mirrors `shared/deferredAnalysis.ts`'s shape — see that file for the
 * sibling analysis pipeline that this pattern is modeled after.
 */

import { safeDeferredAnalysisIdPart, timestampToMillis } from "./deferredAnalysis.js";

export type ExportJobStatus =
  | "initializing"
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "expired";

/**
 * Why a job ended in `status: "failed"`. The UI uses this to pick canned
 * suggestion copy — `"timeout"` shows the "uncheck media types you don't
 * need" banner; `"unknown"` shows a generic failure + support email.
 */
export type ExportJobFailureKind = "timeout" | "unknown" | "capture-download";

export type ExportJobPhase = "text" | "media" | "annotated" | "audio" | "finalizing";

export interface ExportJobIdParts {
  trialId: string;
  experimentId: string;
  taskId: string;
}

export const STALE_INITIALIZING_EXPORT_JOB_MS = 5 * 60 * 1000;

/**
 * A `running` export job that has not written progress for this long is
 * presumed dead (worker OOM, host crash, Cloud Tasks dispatched twice but
 * both attempts vanished, etc.). The worker writes progress every ~2 seconds
 * via `shouldThrottleExportJobProgress`, but the finalizing phase (archive
 * finalize + GCS upload) is a single span with no progress writes. 30 minutes
 * is comfortably longer than the slowest expected upload.
 */
export const STALE_RUNNING_EXPORT_JOB_MS = 30 * 60 * 1000;

/**
 * A `queued` export job that has not transitioned to `running` for this long
 * is presumed stuck — either Cloud Tasks lost the dispatch, the worker
 * crashed at startup before the running-state write, or the queue has been
 * congested for an unreasonably long time. Two hours is comfortably longer
 * than even a backlogged queue (`maxConcurrentDispatches: 5`, ~15 min per
 * large export) while still catching truly dead jobs.
 */
export const STALE_QUEUED_EXPORT_JOB_MS = 2 * 60 * 60 * 1000;

const ACTIVE_EXPORT_JOB_STATUSES: ReadonlySet<ExportJobStatus> = new Set([
  "initializing",
  "queued",
  "running",
]);

export function buildExportJobId({ trialId, experimentId, taskId }: ExportJobIdParts): string {
  return [
    "export",
    safeDeferredAnalysisIdPart(trialId),
    safeDeferredAnalysisIdPart(experimentId),
    safeDeferredAnalysisIdPart(taskId),
  ].join("_");
}

export function isActiveExportJobStatus(status: ExportJobStatus | string | undefined | null): boolean {
  return typeof status === "string" && ACTIVE_EXPORT_JOB_STATUSES.has(status as ExportJobStatus);
}

interface MaybeStaleJob {
  status?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export function isStaleInitializingExportJob(data: MaybeStaleJob, nowMs: number): boolean {
  if (data?.status !== "initializing") return false;
  const reference = timestampToMillis(data.updatedAt) ?? timestampToMillis(data.createdAt);
  if (reference === undefined) return false;
  return nowMs - reference > STALE_INITIALIZING_EXPORT_JOB_MS;
}

export function isStaleRunningExportJob(data: MaybeStaleJob, nowMs: number): boolean {
  if (data?.status !== "running") return false;
  const reference = timestampToMillis(data.updatedAt) ?? timestampToMillis(data.createdAt);
  if (reference === undefined) return false;
  return nowMs - reference > STALE_RUNNING_EXPORT_JOB_MS;
}

export function isStaleQueuedExportJob(data: MaybeStaleJob, nowMs: number): boolean {
  if (data?.status !== "queued") return false;
  const reference = timestampToMillis(data.updatedAt) ?? timestampToMillis(data.createdAt);
  if (reference === undefined) return false;
  return nowMs - reference > STALE_QUEUED_EXPORT_JOB_MS;
}

/**
 * Is this export job no longer downloadable because it has expired?
 *
 * The storage lifecycle rule deletes the ZIP under `exports/` once it is
 * ~1 day old, and the job doc carries `expiresAt = completedAt + 24h`. The
 * client uses this to stop advertising "Download Ready" for a file the
 * lifecycle has (or soon will have) deleted, instead of waiting for the
 * server to flip the doc to `expired` lazily on the next download attempt.
 *
 * This is the client-side superset of the server's `isExportJobObjectExpired`
 * (`functions/src/lib/exportJobLogic.ts`): it ALSO returns true when the doc
 * is already `status: "expired"`. It is intentionally NOT the authority —
 * absent/unparseable `expiresAt` returns false (absence is not expiry), and
 * the server's object-existence probe remains the source of truth on the
 * actual download path.
 */
export function isExportJobExpired(
  job: { status?: unknown; expiresAt?: unknown } | null | undefined,
  nowMs: number
): boolean {
  if (!job) return false;
  if (job.status === "expired") return true;
  const expiresAtMs = timestampToMillis(job.expiresAt);
  if (expiresAtMs === undefined) return false;
  return nowMs >= expiresAtMs;
}

// ============================================================================
// EXPORT INPUTS UPLOAD
//
// The export's generated text (CSVs, COCO JSON) and media manifests are
// uploaded to Storage and referenced by id rather than inlined in the
// `startExportJob` callable body, which Cloud Run rejects past 32 MiB with a
// bare 413 that carries no CORS headers. Full account:
// docs/SERVER_SIDE_ZIP_EXPORT.md → "Export inputs go to Storage".
//
// Shared so the client's upload path and the function's read path cannot drift.
// ============================================================================

/** Plain lowercase UUID v4 — no separators, so an id can never escape the prefix. */
const EXPORT_INPUTS_UPLOAD_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function isValidExportInputsUploadId(uploadId: unknown): uploadId is string {
  return typeof uploadId === "string" && EXPORT_INPUTS_UPLOAD_ID_PATTERN.test(uploadId);
}

/**
 * Kept under the same `exports/` prefix as the built ZIPs so the bucket
 * lifecycle rule reaps uploads too. Matched by the
 * `exports/{orgId}/uploads/{uploadId}` block in storage.rules.
 */
export function exportInputsUploadPath(orgId: string, uploadId: string): string {
  return `exports/${orgId}/uploads/${uploadId}.json`;
}

/**
 * Largest export-inputs upload we accept.
 *
 * Three things must move together, and only the first is machine-enforced:
 *   1. this constant — the client pre-flight check below,
 *   2. the `request.resource.size` bound in storage.rules (CEL cannot import,
 *      so that literal is an unavoidable duplicate — storage.rules.test.ts
 *      pins the boundary),
 *   3. `startExportJob`'s configured memory **at concurrency 1**, since it
 *      JSON.parses the whole object and parsed JS objects run several times
 *      their byte size. The binding is only meaningful while that function
 *      handles one request per instance — raise its concurrency and no memory
 *      setting makes this ceiling safe.
 */
export const EXPORT_INPUTS_MAX_BYTES = 256 * 1024 * 1024;

/**
 * Reason this payload cannot be submitted, or null if it fits. Pure so the
 * boundary is testable without allocating a quarter-gigabyte string.
 */
export function exportInputsSizeError(bytes: number): string | null {
  if (bytes < EXPORT_INPUTS_MAX_BYTES) return null;
  return (
    `Export is too large to submit: ${bytes} bytes exceeds the ${EXPORT_INPUTS_MAX_BYTES}-byte limit. ` +
    `Export fewer traits or split the task.`
  );
}

/**
 * Name of part N (1-based) when the export worker cannot join a plot's transcription WAVs and
 * writes them separately: {name}--part{N}.wav. The browser sizes export paths with it too.
 */
export function combinedAudioPartZipPath(combinedZipPath: string, partNumber: number): string {
  return combinedZipPath.replace(/\.wav$/, `--part${partNumber}.wav`);
}
